import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Flex, FlexItem, FormGroup, Grid, GridItem, Skeleton, Stack, StackItem } from '@patternfly/react-core';
import _ from 'lodash';
import { useLocation, useParams, useHistory } from 'react-router-dom';
import queryString from 'query-string';


import useBoolean from 'utils/hooks/useBoolean';
import useAlerts from 'utils/hooks/useAlerts';
import useApiCall from 'services/useApiCall';
import useAPI from 'services/useAPI';

import Tl from 'localization/LocalizationKeys';
import Tabs from 'components/Tabs/Tabs';
import ModelType from 'components/Model/ModelType';
import ConfirmModalWithOrg from 'components/Modals/ConfirmModalWithOrg';
import ModelsContainer from 'components/Model/ModelsContainer';
import { filterModelsByPath, hasValidInput } from 'components/Model/ModelUtils';
import { useTranslation } from 'localization/useTranslation';

import { TelemetryTab } from './TelemetryTab';
import { CommandTab } from './CommandTab';
import { PropertyTab } from './PropertyTab';

import {
  createBindingId,
  createModelId,
  BindingDescriptionIdError
} from 'components/Admin/Model/utils/model';
import SimpleTable from 'components/Common/SimpleTable/SimpleTable';

import ListSelectModal from 'components/Admin/Model/utils/ListSelectModal';
import useBindingModel from './useBindingModel';
import ContentPage from 'components/ContentView/ContentPage/ContentPage';
import BreadcrumbSkeleton from 'components/Breadcrumb/BreadcrumbSkeleton';
import { useBindingQuery } from '../queries';
import { privileges } from 'utils/types';

const sourceSelectColumns = [
  {
    label: Tl.common.displayname,
    path: 'displayName',
    searchItem: true
  },
  {
    label: Tl.model.id,
    path: 'id',
    searchItem: true
  }
];
let name = '';
for (const b = 36; name.length < 4;) {
  name += (Math.random() * b | 0).toString(b);
}

/*
const getBindingRequest = (api) => {
  return ({ queryKey, signal }) => {
    const newRequest = (bindingId, isDraft) => {
      if (isDraft) {
        return api.models.getBindingDraft(signal, bindingId);
      }
      return api.models.getBinding(signal, bindingId);
    };
    return newRequest(..._.tail(queryKey));
  };
};
*/
export const BindingName = ({ unused_modelId, bindingId }) => {
  const location = useLocation();
  const { bindingDraft } = queryString.parse(location.search, { parseBooleans: true });
  const { binding, isLoading } = useBindingQuery(bindingId, bindingDraft);
  return isLoading ? <BreadcrumbSkeleton /> : (binding?.displayName ?? '');
};

const AdminBindingPage = ({ breadcrumbs }) => {
  const api = useAPI();
  const [alerts, addSuccessAlert, addErrorAlert] = useAlerts();
  const location = useLocation();
  const history = useHistory();
  const { modelId, bindingId } = useParams();
  const { modelDraft, bindingDraft } = queryString.parse(location.search, { parseBooleans: true });
  const {
    telemetryBindings,
    commandBindings,
    propertyBindings,
    // sourceModels,
    assetModels,
    modelInfo,
    changesInModel,
    modelEnvelope,
    isSavingDraft,
    setTelemetryBindings,
    setCommandBindings,
    setPropertyBindings,
    setModelInfo,
    updateSourceModels,
    updateDraft,
    isLoading: isBindingLoading
  } = useBindingModel({
    // Wait for queryBinding to finish before setting bindingId and triggering fetch
    bindingId,
    bindingDraft, modelDraft, modelId, addSuccessAlert, addErrorAlert });

  const isLoading = isBindingLoading;

  const [saveEnabled, setSaveEnabled] = useState(false);
  const [publishEnable, setPublishEnable] = useState(false);
  const [isOpen, showPublishDialog, hidePublishDialog] = useBoolean();
  const [existingPublishedModel, setExistingPublishedModel] = useState(false);

  const [isSourcesOpen, showSourcesDialog, hideSourcesDialog] = useBoolean();
  const T = useTranslation();

  // const [updateDraft, isSavingDraft] = useApiCall({
  //   apiCall: api.models.updateBindingDraft,
  //   onSuccess: useCallback(() => {
  //     addSuccessAlert('Saved Model');
  //   }, [addSuccessAlert]),
  //   onError: useCallback(() => {
  //     addErrorAlert('Failed to save model');
  //   }, [addErrorAlert])
  // });

  const [getBinding] = useApiCall({
    apiCall: api.core.getBinding,
    onSuccess: useCallback(() => {
      setExistingPublishedModel(true);
      showPublishDialog();
    }, [showPublishDialog]),
    onError: useCallback(() => {
      setExistingPublishedModel(false);
      showPublishDialog();
    }, [showPublishDialog])
  });

  const [migrateAssetBinding] = useApiCall({
    apiCall: api.assets.migrateAssetBinding,
    onSuccess: useCallback(() => {
      addSuccessAlert('Assets are migrated to new binding image');
    }, [addSuccessAlert]),
    onError: useCallback((error) => {
      addErrorAlert(error);
      console.error(error);
    }, [addErrorAlert])
  });

  const [validateAssetBinding] = useApiCall({
    apiCall: api.core.validateAssetBinding,
    onSuccess: useCallback(({ assetIds, validation }) => {
      if (_.isEmpty(assetIds)) {
        return;
      }
      if (validation.hasError === true) {
        addErrorAlert('Failed validation');
        return;
      }
      migrateAssetBinding(assetIds, modelEnvelope);
    }, [addErrorAlert, migrateAssetBinding, modelEnvelope]),
    onError: useCallback((error) => {
      console.error(error);
    }, [])
  });


  const [publishDraft, isPublishingDraft] = useApiCall({
    apiCall: api.core.promoteBindingDraft,
    onSuccess: useCallback(() => {
      addSuccessAlert(T.common.published.model);

      history.replace({
        pathname: location.pathname,
        search: queryString.stringify({ modelDraft, bindingDraft: false }),
        state: location.state
      });

      validateAssetBinding(modelEnvelope);
    }, [T.common.published.model, addSuccessAlert, history, location.pathname, location.state, modelDraft, modelEnvelope, validateAssetBinding]),
    onError: useCallback((error) => {
      addErrorAlert(T.common.published.model_failed);
      console.error(error);
    }, [T.common.published.model_failed, addErrorAlert])
  });

  const onAddSourceModels = useCallback(() => {
    showSourcesDialog();
  }, [showSourcesDialog]);

  const modelIdError = useCallback((id) => {
    const modelId = createModelId(id, modelInfo?.version);
    const error = BindingDescriptionIdError(modelId);
    if (error) {
      return true;
    }
  }, [modelInfo?.version]);

  const modelVersionError = useCallback((version) => {
    const modelId = createModelId(modelInfo?.id, version);
    const error = BindingDescriptionIdError(modelId);
    if (error) {
      return true;
    }
  }, [modelInfo?.id]);

  const tabs = useMemo(() => {
    return [
      {
        label: Tl.model.tab.telemetry,
        component: TelemetryTab,
        props: {
          telemetryBindings: telemetryBindings,
          onUpdate: setTelemetryBindings,
          sourceMap: modelInfo?.sources
        }
      },
      // TODO Enable when property bindings exist
      {
        label: Tl.model.tab.properties,
        component: PropertyTab,
        props: {
          propertyBindings: propertyBindings,
          onUpdate: setPropertyBindings,
          sourceMap: modelInfo?.sources
        }
      },
      {
        label: Tl.model.tab.commands,
        component: CommandTab,
        props: {
          commandBindings: commandBindings,
          onUpdate: setCommandBindings,
          sourceMap: modelInfo?.sources
        }
      }
    ];
  }, [telemetryBindings, setTelemetryBindings, modelInfo?.sources, propertyBindings, setPropertyBindings, commandBindings, setCommandBindings]);


  const updateSelectedSourceModels = useCallback((source) => {
    const sources = [...source];
    updateSourceModels(sources);
    hideSourcesDialog();
  }, [hideSourcesDialog, updateSourceModels]);

  const checkOverwrite = useCallback(() => {
    getBinding(createBindingId(modelInfo?.id, modelInfo?.version));
  }, [getBinding, modelInfo?.id, modelInfo?.version]);


  const onSave = useCallback(() => {
    updateDraft();
  }, [updateDraft]);

  const model = useMemo(() => {
    return [
      {
        type: ModelType.TEXT,
        label: Tl.model.id,
        path: 'id',
        isRequired: true,
        hasError: modelIdError
      },
      {
        type: ModelType.TEXT,
        label: Tl.model.version,
        path: 'version',
        isRequired: true,
        hasError: modelVersionError
      },
      {
        type: ModelType.TEXT,
        label: Tl.model.displayName,
        path: 'displayName',
        isRequired: true,
        hasError: _.isEmpty
      },
      {
        type: ModelType.TEXT,
        label: Tl.model.type,
        path: 'modelType',
        disabled: true
      },
      {
        type: ModelType.TEXTAREA,
        label: Tl.model.description,
        path: 'description',
        isRequired: false
      },
      {
        type: ModelType.TEXT,
        label: Tl.model.binding.assetModel,
        path: 'modelId',
        disabled: true
      }
    ];
  }, [modelIdError, modelVersionError]);

  useEffect(function enableSave() {
    setSaveEnabled(hasValidInput(modelInfo, model) && bindingDraft && changesInModel);
  }, [bindingDraft, changesInModel, model, modelInfo]);

  useEffect(function enablePublish() {
    setPublishEnable(!saveEnabled && bindingDraft && hasValidInput(modelInfo, model));
  }, [bindingDraft, model, modelInfo, saveEnabled]);

  const onPublish = useCallback((model, currentOrg) => {
    // TODO: add orgId to binding
    const bindingData = {
      bindingId: model.id,
      orgId: currentOrg.id
    };
    publishDraft(bindingData);
    hidePublishDialog();
  }, [hidePublishDialog, publishDraft]);

  // const onValidate = useCallback(() => {
  //   validateAssetBinding(modelEnvelope);
  // }, [modelEnvelope, validateAssetBinding]);

  // const onMigrate = useCallback(() => {
  //   migrateAssetBinding(modelEnvelope);
  // }, [migrateAssetBinding, modelEnvelope]);
  const firstSection = useMemo(() =>
    filterModelsByPath(model, ['id', 'version', 'displayName']), [model]);
  const secondSection = useMemo(() =>
    filterModelsByPath(model, ['description', 'modelId']), [model]);
  return (
    <ContentPage breadcrumb={breadcrumbs} help={'admin/models/bindings/edit'}>
      { alerts }
      { isLoading && <Skeleton width={'100%'} /> }
      { !isLoading && <Stack>
        <StackItem>
          <Grid hasGutter >
            <GridItem span={2} rowSpan={3} >
              <ModelsContainer models={firstSection} data={modelInfo} setData={setModelInfo} />
            </GridItem>
            <GridItem span={2} rowSpan={3} >
              <ModelsContainer models={secondSection} data={modelInfo} setData={setModelInfo} />
            </GridItem>
            <GridItem span={2} rowSpan={3} >
              <FormGroup
                title="sources" fieldId={'form-name'}>
                <SimpleTable
                  globalActions={[{ onClick: onAddSourceModels, label: Tl.model.binding.select_sources }]}
                  columns={[{ path: 'displayName' }]}
                  data={modelInfo?.sources ?? []}
                  compact
                />
              </FormGroup>
            </GridItem>
            <GridItem span={1} rowSpan={3} >
              <FormGroup label="" fieldId="form-buttons">
                <Flex direction={{ default: 'column' }}>
                  <FlexItem>
                    <Button isLoading={isSavingDraft} onClick={onSave} isDisabled={!saveEnabled}>{ T.common.save }</Button>
                  </FlexItem>
                  <FlexItem>
                    <Button onClick={checkOverwrite} isDisabled={!publishEnable}>{ T.admin.common.publish }</Button>
                  </FlexItem>
                  { /* <FlexItem>
                    <Button onClick={onValidate} >validate</Button>
                  </FlexItem>
                  <FlexItem>
                    <Button onClick={onMigrate} >migrate</Button>
                  </FlexItem> */ }
                </Flex>
              </FormGroup>
            </GridItem>
          </Grid>
        </StackItem>
        <StackItem className={'tab-container'}>
          <Tabs tabs={tabs} tabClassName={'tab-content'}/>
        </StackItem>
      </Stack>
      }
      <ConfirmModalWithOrg
        title={T.modals.confirm.publish.title}
        icon={(existingPublishedModel) ? 'warning' : 'info'}
        isOpen={isOpen}
        isLoading={isPublishingDraft}
        confirmText={(existingPublishedModel) ? T.admin.common.publish_replace : T.admin.common.publish}
        onConfirm={onPublish}
        item={modelEnvelope}
        onCancel={hidePublishDialog}
        template={(existingPublishedModel) ? T.modals.confirm.publish.content_replace : T.modals.confirm.publish.content}
        args={[modelEnvelope?.displayName]}
        neededPrivilege={privileges.ModelManagement}
      />
      <ListSelectModal
        title="Select Source"
        isOpen={isSourcesOpen}
        onCancel={hideSourcesDialog}
        onConfirm={updateSelectedSourceModels}
        columns={sourceSelectColumns}
        data={assetModels}
      />
    </ContentPage>
  );
};

export default AdminBindingPage;
